import { AppRoute } from '../../enums';
import { ConverterService } from '../index';
import { CredentialError } from '../../types';
import { CredentialsService } from '../credentials/credentials.service';
import { CryptoProvider } from './crypto';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class EndToEndEncrypter {
  private readonly cryptoService = inject(CryptoProvider);
  private readonly credentialsSevice = inject(CredentialsService);
  private readonly converterService = inject(ConverterService);
  private readonly router = inject(Router);

  /**
   * Decrypts a given Base64 encoded string.
   *
   * @param {string} saltIvCipher - The Base64 encoded string containing the salt, IV, and cipher text.
   * @return {Promise<ArrayBuffer>} - A promise that resolves to the decrypted data as an ArrayBuffer.
   */
  public async decryptFromBase64(saltIvCipher: string): Promise<ArrayBuffer> {
    try {
      const passphrase = await this.createPassphrase();

      return this.cryptoService.decryptFromBase64(passphrase, saltIvCipher);
    } catch (err: unknown) {
      if (err instanceof Error) {
        await this.handleEncryptionError(err);
      }
      throw err;
    }
  }

  /**
   * Decrypts the provided encrypted data into a Uint16Array.
   *
   * @param {string | Blob} saltIvCipher - The encrypted data to decrypt, specified as either a string or a Blob.
   * @return {Promise<Uint16Array>} - A promise that resolves to a Uint16Array containing the decrypted data, or rejects if decryption fails.
   */
  public async decryptToUint16(saltIvCipher: string | Blob): Promise<Uint16Array> {
    try {
      const passphrase = await this.createPassphrase();

      return this.cryptoService.decrypt(passphrase, saltIvCipher);
    } catch (err: unknown) {
      if (err instanceof Error) {
        await this.handleEncryptionError(err);
      }
      throw err;
    }
  }

  /**
   * Decrypts the provided data to a Uint8Array format.
   *
   * @param {string | Blob} saltIvCipher - The encrypted data to be decrypted, either as a string or a Blob.
   * @return {Promise<Uint8Array>} A promise that resolves to the decrypted data in Uint8Array format.
   */
  public async decryptToUint8(saltIvCipher: string | Blob): Promise<Uint8Array> {
    try {
      const passphrase = await this.createPassphrase();

      return this.cryptoService.decrypt(passphrase, saltIvCipher, Uint8Array);
    } catch (err: unknown) {
      if (err instanceof Error) {
        await this.handleEncryptionError(err);
      }
      throw err;
    }
  }

  /**
   * Encrypts the provided string and returns the result as a Base64 encoded string.
   *
   * @param {string} saltIvCipher - The string to be encrypted.
   * @return {Promise<string>} A promise that resolves to a Base64 encoded string representing the encrypted data.
   */
  public async encryptToBase64(saltIvCipher: string): Promise<string | null> {
    try {
      const passphrase = await this.createPassphrase();
      const dataToEncrypt: Uint16Array = this.converterService.stringToUint16Array(saltIvCipher);

      return this.cryptoService.encryptToBase64(passphrase, dataToEncrypt);
    } catch (err: unknown) {
      if (err instanceof Error) {
        await this.handleEncryptionError(err);
      }
      throw err;
    }
  }

  /**
   * Encrypts the given input and converts the result to a hexadecimal string.
   *
   * @param {string | Uint8Array} saltIvCipher - The input data, which could be a string or a Uint8Array, to be encrypted.
   * @return {Promise<string>} A promise that resolves to the encrypted hexadecimal string.
   */
  public async encryptToHex(saltIvCipher: string | Uint8Array): Promise<string> {
    try {
      const passphrase = await this.createPassphrase();

      return this.cryptoService.encryptToHex(passphrase, saltIvCipher);
    } catch (err: unknown) {
      if (err instanceof Error) {
        await this.handleEncryptionError(err);
      }
      throw err;
    }
  }

  private async createPassphrase(): Promise<string> {
    const password = await this.credentialsSevice.getUserSecret();

    if (password?.length === 0) {
      throw new CredentialError('USER_SECRET');
    }

    const pinResult = await this.credentialsSevice.requestPin();
    if (!pinResult.success) {
      throw new CredentialError('PIN');
    }

    const pin = pinResult.hasPin ? pinResult.pin : '';
    return password + pin;
  }

  private async handleEncryptionError(error: Error): Promise<boolean> {
    if (error instanceof CredentialError && error.reason === 'PIN') {
      // issue with incorrect PIN, navigate to PIN Enter site
      await this.resetPin();
      return this.router.navigate([AppRoute.PinValidation]);
    }

    // some other error with encryption, possibly pwd or PIN (not handled correctly)
    // we should log out and remove PIN, so that the user has to enter both correct
    await this.resetPin();
    return this.router.navigate([AppRoute.Logout]);
  }

  private resetPin(): Promise<void> {
    return this.credentialsSevice.resetPin();
  }
}
